import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Login Failed', {
        description: 'Invalid credentials. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://images.unsplash.com/photo-1762778013301-792c66e710a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwb29sJTIwcmVzb3J0JTIwbG91bmdlJTIwY2hhaXJzfGVufDB8fHx8MTc3MzMyMDYwMXww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm border-[#E7E5E4]" data-testid="login-card">
        <CardContent className="pt-8">
          <div className="flex items-center justify-center mb-8">
            <span className="font-serif tracking-wide whitespace-nowrap text-3xl md:text-4xl text-[#1C1917]" data-testid="brand-name">Acqualina Resort</span>
          </div>

          <h2 className="text-2xl font-serif text-center text-[#1C1917] mb-8" data-testid="login-title">
            Staff Portal
          </h2>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                data-testid="input-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#9F1239] hover:bg-[#881337] text-white rounded-none px-8 py-6 font-serif tracking-wide transition-all duration-300 shadow-md hover:shadow-xl"
              data-testid="btn-login"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Logging in...</>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#0F766E] hover:text-[#9F1239] hover:bg-transparent"
              data-testid="btn-back-home"
            >
              ← Back to Guest Portal
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
